import {Nameable} from "../common/Nameable";
import {Identifier, makeNumericId} from "../common/Identifier";
import {Module} from "../common/Module";
import {SoftDeletable} from "../common/SoftDeletable";
import {OptionType} from "../../components/form/SearchableSelect";
import {isPrivileged} from "../../config/Privileges";
import {CONTRACTOR_AREA_NAME} from "./Contractor";
import {ExternalWorkGroupProp} from "./ExternalWorkGroup";
import {ColumnBuilder, ColumnList} from "../common/DataTable";
import {boolColumn, stringColumn} from "../common/DataTableColumnDefinitions";
import {Language} from "../../config/LanguageConfig";


/**
 * Module for external work
 */
export const ExternalWorkModule: Module = {
    identifier: "external_work",
    isPrivileged: (authUser, privilege) => isPrivileged(
        authUser,
        CONTRACTOR_AREA_NAME,
        ExternalWorkModule.identifier,
        privilege
    )
};

/**
 * ExternalWork unique identifier prop type
 */
export interface ExternalWorkId {
    externalWorkId: Identifier<number>
}

/**
 * ExternalWork describes the kind of work the contractor is hired to do
 */
export interface ExternalWork extends Nameable, ExternalWorkId, ExternalWorkGroupProp, SoftDeletable {
    /**
     * When Software is used in context of Architecture/Construction in germany
     * per german HOAI laws, some work has to be done within those
     * confines and thus must have "Leistungsphasen"
     * most of these "besondere Leistungen" have no phases requirement, so this value defaults to false
     * */
    hasPhases: boolean
}

export type ExternalWorkProp = { externalWork: ExternalWork }

/**
 * external work select option api route
 *
 * @param externalWorkGroupId to filter the response by corresponding group
 * @param contractorId
 */
export function externalWorkSelectRoute( externalWorkGroupId: number = 0, contractorId: number | undefined = 0 ): string {
    return `external-work/select/${ externalWorkGroupId }/${ contractorId ?? 0 }`;
}

export interface ExternalWorkOptionType extends OptionType {
    hasPhases: boolean
}

/**
 * default object of type ExternalWork for usage with forms
 */
export function defaultExternalWork(): ExternalWork {
    return {
        name: "",
        externalWorkId: makeNumericId( 0 ),
        hasPhases: false,
        isDeleted: false,
        externalWorkGroup: {
            externalWorkGroupId: makeNumericId( 0 ),
            name: "",
            shorthand: "",
            area: { name: "", externalWorkAreaId: makeNumericId( 0 ), shorthand: "" },
            numberOfPhases: 0,
            phasesPerTask: [],
            isDeleted: false
        }
    };
}

/**
 * function to create columns for DataTable component
 */

export function externalWorkColumns( language: Language ): ColumnList {
    const colBuilder = new ColumnBuilder();
    return [
        stringColumn( colBuilder )
            .withName('name')
            .withHeadline( language.common.name )
            .build(),
        stringColumn( colBuilder )
            .withName('externalWorkGroup')
            .withHeadline( language.externalWorkGroup.title )
            .setDataAccessor( cellData => cellData.rowData.externalWorkGroup.name )
            .build(),
        boolColumn( colBuilder )
            .withName('hasPhases')
            .withHeadline( language.externalWork.hasPhases.buttonText )
            .build(),
    ];
}